import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { RefreshCw, Repeat, XCircle, PauseCircle, PlayCircle, Copy, Wallet, Plus, X, Gift, CheckCircle2 } from "lucide-react";
import { useT } from "@/context/I18nContext";

export default function Account() {
  const { user, logout, loading } = useAuth();
  const { add, setOpen } = useCart();
  const { t } = useT();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [ref, setRef] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [tracking, setTracking] = useState({});
  const [addonSub, setAddonSub] = useState(null);
  const [addonProducts, setAddonProducts] = useState([]);
  const [addonPick, setAddonPick] = useState({});
  const nav = useNavigate();

  const load = () => {
    api.get("/orders/me").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/subscriptions/me").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/business/quotes/me").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/loyalty/rewards/me").then((r) => setRewards(r.data)).catch(() => {});
  };

  // Fetch tracking (ETA + status) for any order that has a voucher or a shipping country
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = {};
      for (const o of orders) {
        if (o.status === "cancelled") continue;
        try {
          const { data } = await api.get(`/orders/${o.order_number}/tracking`);
          if (cancelled) return;
          results[o.order_number] = data;
        } catch { /* silent */ }
      }
      if (!cancelled) setTracking(results);
    })();
    return () => { cancelled = true; };
  }, [orders]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    load();
    api.get("/products").then((r) => setAddonProducts(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [user, loading]);

  const STATUS_LABEL = {
    pending_kitchen: t("account.status.pending_kitchen"),
    preparing: t("account.status.preparing"),
    ready: t("account.status.ready"),
    dispatched: t("account.status.dispatched"),
    delivered: t("account.status.delivered"),
    cancelled: t("account.status.cancelled"),
  };

  const saveAddons = async () => {
    const items = Object.entries(addonPick).filter(([, q]) => q > 0).map(([id, q]) => ({ product_id: id, quantity: q }));
    try {
      await api.post(`/subscriptions/${addonSub.id}/addons`, { items });
      toast.success(t("account.subs.addon.saved"));
      setAddonSub(null); setAddonPick({}); load();
    } catch { toast.error("Save failed"); }
  };

  const reorder = async (o) => {
    for (const it of o.items) {
      const p = { id: it.product_id, slug: it.slug, name: it.name, price_eur: it.price_eur, images: it.image ? [it.image] : [] };
      add(p, it.quantity);
    }
    toast.success(t("account.orders.reordered", { num: o.order_number }));
    try {
      const first = o.items[0];
      if (first) {
        const { data } = await api.get(`/products/${first.slug}/cross-sell`, { params: { limit: 2 } });
        if (data && data.length > 0) {
          const suggest = data[0];
          toast(t("account.orders.pair_prompt", { name: suggest.name }), {
            duration: 8000, description: t("account.orders.pair_desc"),
            action: { label: t("account.orders.pair_add"), onClick: () => add(suggest, 1) },
          });
        }
      }
    } catch { /* silent */ }
    setOpen(true);
  };

  const cancelSub = async (id) => {
    if (!window.confirm(t("account.subs.cancel_confirm"))) return;
    try { await api.post(`/subscriptions/${id}/cancel`); toast.success(t("account.subs.cancelled")); load(); }
    catch { toast.error("Cancel failed"); }
  };
  const pauseSub = async (id, cycles) => {
    try { await api.post(`/subscriptions/${id}/pause?cycles=${cycles}`); toast.success(t("account.subs.paused", { n: cycles, s: cycles > 1 ? "ς" : "" })); load(); }
    catch { toast.error("Pause failed"); }
  };
  const resumeSub = async (id) => {
    try { await api.post(`/subscriptions/${id}/resume`); toast.success(t("account.subs.resumed")); load(); }
    catch { toast.error("Resume failed"); }
  };
  const copyReferral = async () => {
    if (!ref) return;
    const url = `${window.location.origin}/r/${ref.referral_code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("account.referral.copied"));
    } catch {
      toast.error(t("loyalty.rewards.copy_failed"));
    }
  };
  const copyReward = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("loyalty.rewards.copied"));
    } catch {
      toast.error(t("loyalty.rewards.copy_failed"));
    }
  };

  const CADENCE_MAP = {
    "every week": t("account.cadence.every_week"),
    "every 2 weeks": t("account.cadence.every_2_weeks"),
    "every month": t("account.cadence.every_month"),
    "every 2 months": t("account.cadence.every_2_months"),
  };
  const SUB_STATUS = {
    active: t("account.sub_status.active"),
    paused: t("account.sub_status.paused"),
    cancelled: t("account.sub_status.cancelled"),
  };

  if (loading || !user) return <div className="text-center py-24">{t("account.loading")}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="account-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-green-hover)]">{t("account.eyebrow")}</div>
          <h1 className="font-display text-3xl font-bold">{user.name}</h1>
          <div className="text-sm text-[color:var(--brunchi-ink-soft)]">{user.email}</div>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="text-sm underline" data-testid="logout-btn">{t("account.signout")}</button>
      </div>

      {ref && (
        <section className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-2xl border border-[color:var(--brunchi-stone)] p-6" data-testid="referral-card">
            <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)] mb-2">{t("account.referral.eyebrow")}</div>
            <div className="font-display text-lg font-semibold">{t("account.referral.title")}</div>
            <div className="mt-4 flex gap-2">
              <div className="flex-1 px-3 py-2 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-[color:var(--brunchi-cream)] font-mono text-xs truncate" data-testid="referral-link">
                {window.location.origin}/r/{ref.referral_code}
              </div>
              <button onClick={copyReferral} data-testid="referral-copy-btn"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg pill-primary text-xs">
                <Copy size={12} /> {t("account.referral.copy")}
              </button>
            </div>
          </div>
          <div className="bg-[color:var(--brunchi-green-light)] rounded-2xl border border-[color:var(--brunchi-green)] p-6" data-testid="store-credit-card">
            <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)] mb-2">{t("account.credit.eyebrow")}</div>
            <div className="flex items-center gap-3">
              <Wallet size={22} className="text-[color:var(--brunchi-green-hover)]" />
              <div className="font-mono text-2xl font-bold text-[color:var(--brunchi-green-hover)]" data-testid="store-credit-amount">
                {fmtEUR(ref.store_credit_eur)}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[color:var(--brunchi-stone)] p-6" data-testid="loyalty-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)]">{t("account.loyalty.eyebrow")}</div>
              {ref.brunchi_club_active && (
                <div className="text-[10px] uppercase tracking-widest bg-[color:var(--brunchi-green)] text-white px-2 py-0.5 rounded-full" data-testid="brunchi-club-badge">{t("account.loyalty.club_badge")}</div>
              )}
            </div>
            <div className="flex gap-1.5 mb-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                  i < (ref.loyalty_stamps % 5)
                    ? "bg-[color:var(--brunchi-green)] border-[color:var(--brunchi-green)] text-white"
                    : "border-[color:var(--brunchi-stone-2)] text-[color:var(--brunchi-stone-2)]"
                }`} data-testid={`stamp-${i}`}>
                  {i < (ref.loyalty_stamps % 5) ? "★" : ""}
                </div>
              ))}
            </div>
            <div className="text-xs text-[color:var(--brunchi-ink-soft)]">
              {ref.brunchi_club_active
                ? t("account.loyalty.club_active_until", { date: ref.brunchi_club_until?.slice(0, 10) })
                : ref.stamps_to_reward > 0
                  ? t("account.loyalty.to_reward", { n: ref.stamps_to_reward, s: ref.stamps_to_reward !== 1 ? "s" : "", c: ref.stamps_to_club })
                  : t("account.loyalty.unlocked")}
            </div>
          </div>
        </section>
      )}

      {/* Loyalty rewards (redeemable codes) */}
      <section className="mb-10" data-testid="loyalty-rewards-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Gift size={20} className="text-[color:var(--brunchi-green)]" />
            {t("loyalty.rewards.title")}
          </h2>
          <div className="text-xs text-[color:var(--brunchi-ink-soft)] hidden sm:block">{t("loyalty.rewards.help")}</div>
        </div>
        {rewards.length === 0 ? (
          <div className="text-sm text-[color:var(--brunchi-ink-soft)] p-6 bg-[color:var(--brunchi-cream)] rounded-xl" data-testid="loyalty-rewards-empty">
            {t("loyalty.rewards.empty")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="loyalty-rewards-list">
            {rewards.map((r) => {
              const valueLabel = r.discount_type === "percent"
                ? t("loyalty.rewards.value_percent", { value: r.value })
                : t("loyalty.rewards.value_fixed", { value: Number(r.value).toFixed(0) });
              return (
                <div key={r.id}
                  className={`rounded-xl border p-4 ${r.is_available
                    ? "bg-[color:var(--brunchi-green-light)] border-[color:var(--brunchi-green)]"
                    : "bg-[color:var(--brunchi-cream-2)] border-[color:var(--brunchi-stone)] opacity-70"}`}
                  data-testid={`loyalty-reward-${r.code}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-[10px] uppercase tracking-widest ${r.is_available ? "text-[color:var(--brunchi-green-hover)]" : "text-[color:var(--brunchi-ink-soft)]"}`}>
                      {r.is_available ? t("loyalty.rewards.available") : t("loyalty.rewards.used")}
                    </div>
                    {!r.is_available && <CheckCircle2 size={14} className="text-[color:var(--brunchi-ink-soft)]" />}
                  </div>
                  <div className="font-mono text-lg font-bold tracking-wider break-all" data-testid={`loyalty-reward-code-${r.code}`}>
                    {r.code}
                  </div>
                  <div className="text-xs text-[color:var(--brunchi-ink-soft)] mt-1">{valueLabel}</div>
                  {r.is_available && (
                    <button onClick={() => copyReward(r.code)}
                      data-testid={`loyalty-reward-copy-${r.code}`}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-primary text-xs">
                      <Copy size={12} /> {t("loyalty.rewards.copy")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">{t("account.subs.title")}</h2>
          <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{t("account.subs.help")}</div>
        </div>
        {subs.length === 0 ? (
          <div className="text-sm text-[color:var(--brunchi-ink-soft)] p-6 bg-[color:var(--brunchi-cream)] rounded-xl">
            {t("account.subs.empty")}
          </div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid={`sub-${s.id}`}>
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Repeat size={20} className="text-[color:var(--brunchi-green)]" />
                    <div>
                      <div className="font-display text-base">{CADENCE_MAP[s.cadence] || s.cadence}</div>
                      <div className="text-xs text-[color:var(--brunchi-ink-soft)]">
                        {t("account.subs.per_cycle", { n: s.items?.length || 0, total: fmtEUR(s.total_eur) })}
                      </div>
                      {s.paused_until && s.status === "paused" && (
                        <div className="text-xs text-[color:var(--brunchi-amber)] mt-1">
                          {t("account.subs.paused_until", { date: s.paused_until.slice(0, 10) })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`text-xs px-3 py-1 rounded-full ${
                    s.status === "active" ? "bg-[color:var(--brunchi-green-light)] text-[color:var(--brunchi-green-hover)]"
                    : s.status === "cancelled" ? "bg-red-50 text-[color:var(--brunchi-error)]"
                    : s.status === "paused" ? "bg-amber-50 text-[color:var(--brunchi-amber)]"
                    : "bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-ink-soft)]"
                  }`}>
                    {SUB_STATUS[s.status] || s.status}
                  </div>
                </div>
                {s.status === "active" && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[color:var(--brunchi-stone)]">
                    <button onClick={() => pauseSub(s.id, 1)} data-testid={`sub-pause-1-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-outline text-xs">
                      <PauseCircle size={14} /> {t("account.subs.pause_1")}
                    </button>
                    <button onClick={() => pauseSub(s.id, 2)} data-testid={`sub-pause-2-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-outline text-xs">
                      <PauseCircle size={14} /> {t("account.subs.pause_2")}
                    </button>
                    <button onClick={() => setAddonSub(s)} data-testid={`sub-addon-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-outline text-xs">
                      <Plus size={14} /> {t("account.subs.add_next")}
                    </button>
                    <button onClick={() => cancelSub(s.id)} data-testid={`sub-cancel-${s.id}`}
                      className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[color:var(--brunchi-error)] hover:bg-red-50">
                      <XCircle size={14} /> {t("account.subs.cancel")}
                    </button>
                  </div>
                )}
                {s.next_addons?.length > 0 && (
                  <div className="mt-2 text-xs text-[color:var(--brunchi-green-hover)]" data-testid={`sub-addons-summary-${s.id}`}>
                    {t("account.subs.next_extras", { n: s.next_addons.length, s: s.next_addons.length !== 1 ? "s" : "" })}
                  </div>
                )}
                {s.status === "paused" && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-[color:var(--brunchi-stone)]">
                    <button onClick={() => resumeSub(s.id)} data-testid={`sub-resume-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-primary text-xs">
                      <PlayCircle size={14} /> {t("account.subs.resume")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <h2 className="font-display text-2xl mb-4">{t("account.orders.title")}</h2>
      {quotes.length > 0 && (
        <section className="mb-8 bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid="wholesale-portal">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg">{t("account.quotes.title")}</div>
            <Link to="/wholesale" className="text-xs underline text-[color:var(--brunchi-green-hover)]">{t("account.quotes.another")}</Link>
          </div>
          <div className="space-y-2 text-sm">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center justify-between border-b border-[color:var(--brunchi-stone)] last:border-0 py-2" data-testid={`quote-${q.id}`}>
                <div>
                  <div className="font-medium">{q.company}</div>
                  <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{q.bar_count} bars · {q.frequency || "one-off"} · {q.created_at?.slice(0, 10)}</div>
                </div>
                <div className={`text-xs px-2 py-1 rounded-full ${
                  q.status === "closed" ? "bg-[color:var(--brunchi-green-light)] text-[color:var(--brunchi-green-hover)]"
                  : q.status === "contacted" ? "bg-amber-50 text-[color:var(--brunchi-amber)]"
                  : "bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-ink-soft)]"
                }`}>{q.status}</div>
              </div>
            ))}
          </div>
        </section>
      )}
      {orders.length === 0 ? (
        <div className="text-sm text-[color:var(--brunchi-ink-soft)] p-8 bg-[color:var(--brunchi-cream)] rounded-xl">
          {t("account.orders.empty_prefix")} <Link to="/shop" className="underline text-[color:var(--brunchi-green-hover)]">{t("account.orders.start")}</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`order-${o.order_number}`}>
              <div>
                <Link to={`/track/${o.order_number}`} className="font-mono text-sm font-semibold hover:text-[color:var(--brunchi-green-hover)]" data-testid={`order-track-${o.order_number}`}>
                  {o.order_number}
                </Link>
                <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{o.created_at?.slice(0, 10)} · {o.items.length} items</div>
              </div>
              <div className="text-xs px-3 py-1 rounded-full bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-forest)]">
                {STATUS_LABEL[o.status] || o.status}
              </div>
              {o.acs_voucher_no && (
                <a href={tracking[o.order_number]?.tracking_url || `https://www.acscourier.net/en/track-and-trace/?VoucherNo=${encodeURIComponent(o.acs_voucher_no)}`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brunchi-green-hover)] hover:underline font-mono"
                   data-testid={`acs-tracking-${o.order_number}`}
                   title={t("account.orders.track_link")}>
                  <span className="uppercase tracking-widest text-[10px] text-[color:var(--brunchi-ink-soft)]">{t("account.orders.tracking")}</span>
                  {o.acs_voucher_no}
                </a>
              )}
              {tracking[o.order_number] && !["cancelled", "delivered"].includes(o.status) && (
                <div className="text-xs px-3 py-1 rounded-full bg-[color:var(--brunchi-cream)] border border-[color:var(--brunchi-stone)]"
                     data-testid={`eta-${o.order_number}`}>
                  <span className="uppercase tracking-widest text-[10px] text-[color:var(--brunchi-ink-soft)] mr-2">{t("account.orders.eta")}</span>
                  <span>{(() => {
                    const tr = tracking[o.order_number];
                    if (tr.summary?.delivered) return t("account.orders.delivered");
                    if (tr.summary?.eta) return t("account.orders.eta_single", { date: String(tr.summary.eta).slice(0, 10) });
                    if (tr.eta_from && tr.eta_to) return t("account.orders.eta_range", { from_date: tr.eta_from, to_date: tr.eta_to });
                    return t("account.orders.in_transit");
                  })()}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <button onClick={() => reorder(o)} data-testid={`reorder-${o.order_number}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full pill-outline text-xs">
                  <RefreshCw size={12} /> {t("account.orders.reorder")}
                </button>
                <div className="font-mono font-semibold">{fmtEUR(o.total_eur)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {addonSub && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAddonSub(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="addon-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brunchi-green-hover)]">{t("account.subs.addon.eyebrow")}</div>
                <div className="font-display text-xl">{addonSub.cadence}</div>
              </div>
              <button onClick={() => setAddonSub(null)} className="p-1.5 rounded-full hover:bg-[color:var(--brunchi-cream-2)]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[color:var(--brunchi-ink-soft)] mb-3">{t("account.subs.addon.help")}</p>
            <div className="space-y-2">
              {addonProducts.map((p) => {
                const q = addonPick[p.id] || 0;
                return (
                  <div key={p.id} className="flex items-center gap-3 bg-[color:var(--brunchi-cream)] rounded-lg p-2" data-testid={`addon-product-${p.slug}`}>
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-12 h-12 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-xs text-[color:var(--brunchi-ink-soft)]">{fmtEUR(p.price_eur)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setAddonPick({ ...addonPick, [p.id]: Math.max(0, q - 1) })}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)]"><span className="text-xs">−</span></button>
                      <span className="font-mono text-sm w-5 text-center" data-testid={`addon-qty-${p.slug}`}>{q}</span>
                      <button onClick={() => setAddonPick({ ...addonPick, [p.id]: q + 1 })}
                        className="w-7 h-7 rounded-full border border-[color:var(--brunchi-stone-2)]"><span className="text-xs">+</span></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={saveAddons} data-testid="addon-save-btn"
              className="mt-5 w-full py-3 rounded-full pill-primary font-medium">{t("account.subs.addon.save")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
